// Shared staff-portal date/time helpers.
//
// Calendar dates (due dates, effective end dates, occurred-on) are stored at
// UTC midnight; formatting them in local time renders a day early in western
// timezones (Jul 31 -> "Jul 30"). Always format calendar dates in UTC. Relative
// "x ago" is elapsed-time math, so it's timezone-agnostic.

// Calendar date, e.g. "Jul 31" or "Jul 31, 2026". Pinned to UTC.
export function fmtDate(iso, { withYear = false } = {}) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
    ...(withYear ? { year: 'numeric' } : {}),
  });
}

// Month + year ONLY, e.g. "Jul 2023". Owner display rule for "With SFLCM
// since" / "Teaching at SFLCM since": the exact day is stored (and editable)
// but NEVER shown — always render start dates through this helper. Pinned to
// UTC like the other calendar-date helpers.
export function fmtMonthYear(iso) {
  if (!iso) return '';
  const d = new Date(String(iso).slice(0, 10));
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', year: 'numeric' });
}

// Tenure duration from a start date, months precision, zero-suppressed:
// "3 yrs", "2 yrs 11 mos", "8 mos", "1 yr", "1 mo". Month-anchored to match
// fmtMonthYear (days are ignored — "since Jul 2023" reads as 3 yrs in Jul 2026
// regardless of the stored day). Returns '' for missing/unparseable/future
// dates and for a start in the current month (no "0 mos").
export function tenureLabel(iso, now = new Date()) {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})/.exec(String(iso).slice(0, 10));
  if (!m) return '';
  const months = (now.getFullYear() - Number(m[1])) * 12 + (now.getMonth() + 1 - Number(m[2]));
  if (!Number.isFinite(months) || months <= 0) return '';
  const yrs = Math.floor(months / 12);
  const mos = months % 12;
  const parts = [];
  if (yrs > 0) parts.push(`${yrs} ${yrs === 1 ? 'yr' : 'yrs'}`);
  if (mos > 0) parts.push(`${mos} ${mos === 1 ? 'mo' : 'mos'}`);
  return parts.join(' ');
}

// Date + time for event timestamps, e.g. "Jul 31, 2026, 2:30 PM" in UTC.
// Labeled UTC so a viewer in another timezone isn't misled.
export function fmtDateTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.toLocaleString(undefined, {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })} UTC`;
}

// Date + time in Miami time, e.g. "Jul 31, 2026, 10:30 AM ET". Use this for
// event timestamps shown to humans (issue occurred/logged, comments) — the
// staff are all in Florida and shouldn't have to convert from UTC.
// IMPORTANT: calendar-date helpers (fmtDate / fmtTime / dateRange) stay pinned
// to UTC BY DESIGN — date-only values are stored at UTC midnight and rendering
// them in a local zone shifts them a day. Do not "fix" those to ET.
export function fmtDateTimeET(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.toLocaleString(undefined, {
    timeZone: 'America/New_York',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })} ET`;
}

// Time-of-day, e.g. "2:30 PM". Pinned to UTC to match the calendar-date helpers
// above (timestamps are stored/compared in UTC; rendering local would drift the
// day for same-day ranges that fmtDate already pins to UTC).
export function fmtTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString(undefined, { timeZone: 'UTC', hour: 'numeric', minute: '2-digit' });
}

// Human range across one or two timestamps:
//   single date        -> "Jul 31, 2026"
//   same-day w/ times   -> "Jul 31, 2026 · 9:00 AM – 12:00 PM"
//   multi-day           -> "Jul 31 – Aug 3, 2026"
export function dateRange(startIso, endIso) {
  if (!startIso) return 'Date TBD';
  const start = new Date(startIso);
  // NaN-guard BOTH ends (mirrors fmtDate's guard): toISOString() on an Invalid
  // Date throws a RangeError, so a present-but-unparseable startIso used to
  // crash the whole render instead of degrading.
  if (Number.isNaN(start.getTime())) return 'Date TBD';
  const endParsed = endIso ? new Date(endIso) : null;
  const end = endParsed && !Number.isNaN(endParsed.getTime()) ? endParsed : null;
  // Compare in UTC so a same-day range isn't split by a local-vs-UTC date shift.
  const sameDay = end && start.toISOString().slice(0, 10) === end.toISOString().slice(0, 10);
  if (!end) return fmtDate(startIso, { withYear: true });
  if (sameDay) {
    return `${fmtDate(startIso, { withYear: true })} · ${fmtTime(startIso)} – ${fmtTime(endIso)}`;
  }
  return `${fmtDate(startIso)} – ${fmtDate(endIso, { withYear: true })}`;
}

// ── School-local (ET) calendar dates — for Opus1 ABSENCE WINDOWS ─────────────
// Ported from the teacher portal's fix (api/teach/timeoff.js dateOnly, the
// 2026-07-12 launch-sprint finding): Opus1 stores a time-off's END as the LAST
// INSTANT of the final day in ET (e.g. …T03:59:59.999Z = 23:59:59.999 ET),
// which is already the NEXT day in UTC; the START is ET midnight (same UTC
// date). The UTC-pinned helpers above therefore rendered every one-day request
// as a two-day span ("Aug 11 – Aug 12" for a Tue Aug 11 request) — the exact
// P1-4 defect from the 2026-08-03 time-off audit (ticket 892b9f9c), and the
// same misread that poisoned the Jul-31 staffing audit (12/11 vs the true 7/6).
//
// SCOPE: use these ONLY for Opus1 timestamp ranges that mean "an ET calendar
// window" (time-off start_at/end_at). Date-ONLY values stored at UTC midnight
// (due dates, effective ends) must keep the UTC-pinned helpers above — see the
// warning on fmtDateTimeET.

// ET calendar date of a timestamp as lexicographic 'YYYY-MM-DD', or null.
export function etDateOnly(ts) {
  if (!ts) return null;
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(d);
}

// "Aug 11" / "Aug 11, 2026" for the ET calendar day of a timestamp.
function fmtEtDay(isoDate, { withYear = false } = {}) {
  if (!isoDate) return '';
  // isoDate is already the resolved ET day — format it as a plain date (UTC
  // pinning on the date-only string keeps it from shifting again).
  return fmtDate(`${isoDate}T00:00:00Z`, { withYear });
}

// Human range over an Opus1 ET absence window:
//   one ET day   -> "Aug 11, 2026"
//   multi-day    -> "Aug 13 – Aug 14, 2026"
// NaN-tolerant like dateRange. End before start degrades to the start day.
export function etDateRange(startIso, endIso) {
  const start = etDateOnly(startIso);
  if (!start) return 'Date TBD';
  const end = etDateOnly(endIso);
  if (!end || end <= start) return fmtEtDay(start, { withYear: true });
  return `${fmtEtDay(start)} – ${fmtEtDay(end, { withYear: true })}`;
}

// The same absence-window rule with an explicit weekday. Request cards use
// this form because the weekday is operationally meaningful at a glance
// ("Tue Aug 11"), and must be derived from the already-resolved ET calendar
// date so it cannot drift to Monday in a western browser timezone.
export function etDateRangeWithWeekday(startIso, endIso) {
  const start = etDateOnly(startIso);
  if (!start) return 'Date TBD';
  const end = etDateOnly(endIso);
  const fmt = (isoDate, { withYear = false } = {}) => {
    const d = new Date(`${isoDate}T00:00:00Z`);
    if (Number.isNaN(d.getTime())) return '';
    const weekday = d.toLocaleDateString('en-US', { timeZone: 'UTC', weekday: 'short' });
    return `${weekday} ${fmtEtDay(isoDate, { withYear })}`;
  };
  if (!end || end <= start) return fmt(start, { withYear: true });
  return `${fmt(start)} – ${fmt(end, { withYear: true })}`;
}

// Inclusive ET calendar-day count of the window ("Aug 13–14" -> 2). 0 when the
// start is missing/unparseable.
export function etDayCount(startIso, endIso) {
  const start = etDateOnly(startIso);
  if (!start) return 0;
  const end = etDateOnly(endIso);
  if (!end || end <= start) return 1;
  const ms = Date.parse(`${end}T00:00:00Z`) - Date.parse(`${start}T00:00:00Z`);
  return Math.round(ms / 86400000) + 1;
}

// Relative "x ago" — elapsed time, timezone-agnostic.
export function timeAgo(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 48) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
}
